// text — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: contents;
  }
`;

export function render(host) {
  const {
    variant,
    size,
    color,
    weight,
    align,
    truncate,
    clamp,
    nowrap,
    _resolvedAs,
  } = host;

  // --- font-family ---
  let fontFamily;
  if (variant === 'heading') {
    fontFamily = 'var(--run-font-semantic-family-heading)';
  } else if (variant === 'code' || variant === 'kbd') {
    fontFamily = 'var(--run-font-semantic-family-code)';
  } else {
    fontFamily = 'var(--run-text-font-family, var(--run-font-semantic-family-body))';
  }

  // --- font-size ---
  const sizeTokenMap = {
    'xs':  'var(--run-font-size-xs)',
    'sm':  'var(--run-font-size-sm)',
    'md':  'var(--run-font-size-base)',
    'lg':  'var(--run-font-size-lg)',
    'xl':  'var(--run-font-size-xl)',
    '2xl': 'var(--run-font-size-2xl)',
    '3xl': 'var(--run-font-size-3xl)',
    '4xl': 'var(--run-font-size-4xl)',
  };
  const fontSize = `var(--run-text-size, ${sizeTokenMap[size] || 'var(--run-font-size-base)'})`;

  // --- font-weight ---
  let fontWeightToken;
  if (weight === 'regular') {
    fontWeightToken = 'var(--run-font-semantic-weight-body)';
  } else if (weight === 'medium') {
    fontWeightToken = 'var(--run-font-semantic-weight-medium)';
  } else if (weight === 'semibold') {
    fontWeightToken = 'var(--run-font-semantic-weight-strong)';
  } else if (weight === 'bold') {
    fontWeightToken = 'var(--run-font-semantic-weight-bold)';
  } else {
    // null — use variant default
    if (variant === 'heading') {
      fontWeightToken = 'var(--run-font-semantic-weight-bold)';
    } else {
      fontWeightToken = 'var(--run-font-semantic-weight-body)';
    }
  }
  const fontWeight = `var(--run-text-weight, ${fontWeightToken})`;

  // --- line-height ---
  let lineHeightToken;
  if (variant === 'heading') {
    lineHeightToken = '1.25';
  } else {
    lineHeightToken = '1.5';
  }
  const lineHeight = `var(--run-text-line-height, ${lineHeightToken})`;

  // --- letter-spacing ---
  const letterSpacing = '0';

  // --- color ---
  let colorToken;
  if (color === 'secondary') {
    colorToken = 'var(--run-color-text-secondary)';
  } else if (color === 'muted') {
    colorToken = 'var(--run-color-text-tertiary)';
  } else if (color === 'brand') {
    colorToken = 'var(--run-color-intent-primary-base)';
  } else if (color === 'success') {
    colorToken = 'var(--run-color-intent-success-base)';
  } else if (color === 'warning') {
    colorToken = 'var(--run-color-intent-warning-base)';
  } else if (color === 'error') {
    colorToken = 'var(--run-color-intent-danger-base)';
  } else if (color === 'inverse') {
    colorToken = 'var(--run-color-text-inverse)';
  } else {
    colorToken = 'var(--run-color-text-primary)';
  }
  const colorValue = `var(--run-text-color, ${colorToken})`;

  // --- text-align ---
  const alignMap = {
    'start':   'start',
    'center':  'center',
    'end':     'end',
    'justify': 'justify',
  };
  const textAlign = alignMap[align] || 'start';

  // --- overflow / wrapping ---
  // clamp takes precedence over truncate per MVA §Part 10
  let overflowStyles = '';
  if (clamp !== null && clamp !== undefined) {
    overflowStyles = [
      'display: -webkit-box',
      '-webkit-box-orient: vertical',
      `-webkit-line-clamp: ${clamp}`,
      'overflow: hidden',
    ].join('; ');
  } else if (truncate) {
    overflowStyles = [
      'overflow: hidden',
      'text-overflow: ellipsis',
      'white-space: nowrap',
    ].join('; ');
  } else if (nowrap) {
    overflowStyles = 'white-space: nowrap';
  }

  // --- kbd extra styles ---
  let kbdStyles = '';
  if (variant === 'kbd') {
    kbdStyles = [
      'background: var(--run-color-surface-subtle)',
      'border: 1px solid var(--run-color-border-default)',
      'border-radius: var(--run-radius-semantic-control)',
      'padding: 0.125em 0.375em',
    ].join('; ');
  }

  // --- assemble inline style ---
  const styleParts = [
    `font-family: ${fontFamily}`,
    `font-size: ${fontSize}`,
    `font-weight: ${fontWeight}`,
    `line-height: ${lineHeight}`,
    `letter-spacing: ${letterSpacing}`,
    `color: ${colorValue}`,
    `text-align: ${textAlign}`,
  ];
  if (overflowStyles) styleParts.push(overflowStyles);
  if (kbdStyles) styleParts.push(kbdStyles);

  const styleAttr = styleParts.join('; ');

  const tag = _resolvedAs || 'span';

  host.shadowRoot.innerHTML = `<${tag} part="text" style="${styleAttr}"><slot></slot></${tag}>`;
}